/**
 * On-disk react-query cache — the thing that makes the app work offline.
 *
 * Every query result is written to IndexedDB and read back at boot, so a cold
 * launch with no connection still renders the library, the documents you have
 * opened, your profile and your preferences. When a connection is there, the
 * normal react-query lifecycle refetches in the background and the screen
 * updates itself — no rebuild, no reinstall, no "pull to refresh".
 *
 * Why IndexedDB and not Capacitor Preferences: Preferences is
 * SharedPreferences / UserDefaults, meant for a few small values, and a whole
 * query cache is megabytes. IndexedDB is available and durable in both the
 * Android WebView and iOS WKWebView, and in every browser for the PWA build.
 *
 * The session token is deliberately NOT here — that lives in Preferences (see
 * lib/platform.ts), which survives a WebView storage clear.
 */
import { del, get, set } from 'idb-keyval';
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';

const CACHE_KEY = 'vu:query-cache';

/** How long a persisted cache stays usable before it is thrown away wholesale. */
export const CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Hand-bumped. Bump ONLY when a cached response shape changes in a way a new
 * build cannot read — the thing a buster legitimately protects against, and a
 * rare event.
 *
 * It used to be `__BUILD_ID__`, so every deploy discarded the whole snapshot
 * "by construction instead of relying on someone remembering to bump it". The
 * intent was right; the blast radius was not. The snapshot holds the SESSION as
 * well as the content, so shipping any change signed the app out of its own
 * cache. The next cold launch then had no `me`: online that meant waiting on
 * `/api/me` with nothing to show, and OFFLINE the fetch failed, `data` was
 * undefined, and the route guard bounced the user to the login screen they had
 * been promised they would never see again.
 */
export const CACHE_SCHEMA = '2';

/** Queries that must OUTLIVE a deploy. The session is identity, not content:
 *  its shape is stable, and losing it is what shows a signed-in user a login
 *  form. Everything else is content, and safe to re-fetch. */
const KEEP_ACROSS_BUILDS = ['me'];

function buildId(): string {
  return typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';
}

function isKeeper(q: { queryKey: unknown }): boolean {
  return Array.isArray(q.queryKey) && KEEP_ACROSS_BUILDS.includes(String(q.queryKey[0]));
}

export function createIdbPersister(key: string = CACHE_KEY): Persister {
  return {
    persistClient: async (client: PersistedClient) => {
      try {
        await set(key, { ...client, vuBuild: buildId() });
      } catch {
        // Quota exceeded or private-mode storage: losing the offline cache is
        // survivable, breaking the running app is not.
      }
    },
    restoreClient: async () => {
      try {
        const client = await get<PersistedClient & { vuBuild?: string }>(key);
        if (!client) return undefined;
        // A NEW BUILD drops the content but KEEPS the session. Discarding the
        // whole snapshot on a shape change is still the provider's job, via its
        // `buster` (now `CACHE_SCHEMA`); this narrower rule is what stops an
        // ordinary deploy from logging the app out of its own cache.
        if (client.vuBuild && client.vuBuild !== buildId()) {
          client.clientState = {
            ...client.clientState,
            queries: (client.clientState.queries ?? []).filter(isKeeper),
            mutations: [],
          };
        }
        return client;
      } catch {
        return undefined;
      }
    },
    removeClient: async () => {
      try {
        await del(key);
      } catch {
        /* nothing to clean up */
      }
    },
  };
}

export const persister = createIdbPersister();

/**
 * Drop the on-disk snapshot.
 *
 * Called on logout: `queryClient.clear()` only empties the in-memory cache, and
 * the persister would happily rehydrate the previous user's data on the next
 * cold launch.
 */
export async function removeQueryCache(): Promise<void> {
  await persister.removeClient();
}
